import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { IProfessionalSituationResource } from "../../professional-situations";
import { professionalSituationQuery } from "../../professional-situations/queries/professionalSituationQuery";
import { useApi } from "../../SDKProvider";
import { ITrainingPhaseResource } from "../../training-phases";
import { createApiQueryHook, IApiQueryFunction, IPaginateable, mapPagination, unwrapAPIResult } from "../../utils/api";
import { mapRelationalValue, RelationalValue } from "../../utils/filters";
import { serialize } from "../../utils/url";
import { ITrainingPhaseProfessionalSituationList, ITrainingPhaseProfessionalSituationResource } from "../types";

export interface ITrainingPhaseProfessionalSituationListQueryParameters extends IPaginateable {
    trainingPhase?: RelationalValue<ITrainingPhaseResource>;
}

export type ITrainingPhaseProfessionalSituationListQueryResult = ITrainingPhaseProfessionalSituationList;

export type IHydratedTrainingPhaseProfessionalSituationResource = Omit<
    ITrainingPhaseProfessionalSituationResource,
    "professionalSituation"
> & {
    professionalSituation: IProfessionalSituationResource;
};

export type IHydratedTrainingPhaseProfessionalSituationListQueryResult =
    IHydratedTrainingPhaseProfessionalSituationResource[];

export const getTrainingPhaseProfessionalSituationListQueryKey = (
    trainingPhase?: RelationalValue<ITrainingPhaseResource>,
): QueryKey => ["training-phase-professional-situations", mapRelationalValue(trainingPhase) ?? null];

export const trainingPhaseProfessionalSituationListQuery: IApiQueryFunction<
    ITrainingPhaseProfessionalSituationListQueryParameters
> = ({ trainingPhase, pagination = false }) => {
    if (trainingPhase == null) {
        return;
    }

    return {
        method: "GET",
        url: `/training_phase_professional_situations${serialize(
            {
                ...mapPagination(pagination),
                "trainingPhase.uuid": mapRelationalValue(trainingPhase),
            },
            true,
        )}`,
    };
};

export const useTrainingPhaseProfessionalSituationListQuery = createApiQueryHook<
    ITrainingPhaseProfessionalSituationListQueryParameters,
    ITrainingPhaseProfessionalSituationListQueryResult
>(trainingPhaseProfessionalSituationListQuery);

export const useHydratedTrainingPhaseProfessionalSituationListQuery = (
    { trainingPhase, pagination = false }: ITrainingPhaseProfessionalSituationListQueryParameters,
    config: UseQueryOptions<IHydratedTrainingPhaseProfessionalSituationListQueryResult> = {},
) => {
    const api = useApi();
    const queryKey = useMemo(() => getTrainingPhaseProfessionalSituationListQueryKey(trainingPhase), [trainingPhase]);

    const queryFn = useCallback(async (): Promise<IHydratedTrainingPhaseProfessionalSituationListQueryResult> => {
        const requestConfig = trainingPhaseProfessionalSituationListQuery({ trainingPhase, pagination });

        if (!requestConfig) {
            return [];
        }

        const professionalSituationLinks = (
            await unwrapAPIResult(api.request<ITrainingPhaseProfessionalSituationList>(requestConfig))
        )["hydra:member"];

        return Promise.all(
            professionalSituationLinks.map(async item => {
                if (typeof item.professionalSituation !== "string") {
                    return item as IHydratedTrainingPhaseProfessionalSituationResource;
                }

                const professionalSituationRequestConfig = professionalSituationQuery({
                    professionalSituation: item.professionalSituation,
                });

                if (!professionalSituationRequestConfig) {
                    throw new Error("Professional situation query config is missing");
                }

                const professionalSituation: IProfessionalSituationResource = await unwrapAPIResult(
                    api.request<IProfessionalSituationResource>(professionalSituationRequestConfig),
                );

                return {
                    ...item,
                    professionalSituation,
                };
            }),
        );
    }, [api, pagination, trainingPhase]);

    return useQuery({
        queryKey,
        queryFn,
        enabled: trainingPhase != null,
        ...config,
    });
};
